import type { Device, Texture } from '@luma.gl/core';
import ShelfPack from '@mapbox/shelf-pack';
import { DEVICE_PIXEL_RATIO } from 'src/lib/player/constants';
import type { FlagContent, PoiType, StarterType } from 'src/lib/player/store';
import { useStore } from 'src/lib/player/store';
import { make2DCanvas, realSubstring } from 'src/lib/player/utils';
import {
  createPoiDrawInfo,
  createStarterDrawInfo,
  type DrawInfo,
  DrawInfoType,
} from './createDrawInfos';
import MarkerContext from './MarkerContext';

const SPRITESHEET_SIZE = 2048;
const LARGE_MODE_SCALE = 2;
// Unselected flags are drawn smaller, not shrunk on the GPU: the atlas has no mipmaps.
const UNSELECTED_SCALE = 0.8;

export type SpriteSheet = {
  index: number;
  width: number;
  height: number;
  canvas: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  texture: Texture | null;
  // The device that owns `texture`. SpriteManagers are memoized singletons and outlive devices.
  device: Device | null;
  shelf: ShelfPack;
};

export type Sprite = {
  hashKey: string;
  lat: number;
  lng: number;
  alpha: number;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  x: number;
  y: number;
  mask: boolean;
  sheetIndex: number;
  _starter?: StarterType;
  _poi?: PoiType;
};

class SpriteManager {
  sprites: { [key: string]: Sprite } = {};
  spritesheets: Array<SpriteSheet>;
  dirtySprites: Set<Sprite>;

  constructor() {
    this.spritesheets = [];
    this.dirtySprites = new Set();
    this.addSpritesheet(this.createSpritesheet(0));
  }

  createSpritesheet(index: number = this.spritesheets.length): SpriteSheet {
    const { canvas, context } = make2DCanvas(
      SPRITESHEET_SIZE,
      SPRITESHEET_SIZE,
      DEVICE_PIXEL_RATIO,
    );
    const shelf = new ShelfPack(
      Math.floor(SPRITESHEET_SIZE / DEVICE_PIXEL_RATIO),
      Math.floor(SPRITESHEET_SIZE / DEVICE_PIXEL_RATIO),
    );
    return {
      index,
      width: SPRITESHEET_SIZE,
      height: SPRITESHEET_SIZE,
      texture: null,
      device: null,
      canvas,
      context,
      shelf,
    };
  }

  addSpritesheet(sprite: SpriteSheet): void {
    this.spritesheets.push(sprite);
  }

  getCurrentSpritesheet(): SpriteSheet {
    return this.spritesheets[this.spritesheets.length - 1];
  }

  createMarker(drawInfo: DrawInfo, hashKey: string, scale = 1): Sprite {
    const measurement = MarkerContext.measure(drawInfo);
    const scaledMeasurement = {
      x: Math.ceil(measurement.x * scale),
      y: Math.ceil(measurement.y * scale),
      offsetX: measurement.offsetX * scale,
      offsetY: measurement.offsetY * scale,
    };
    // One pixel of gap, one for snapping the sprite to whole atlas pixels below.
    let bin = this.getCurrentSpritesheet().shelf.packOne(
      scaledMeasurement.x + 2,
      scaledMeasurement.y + 2,
    );
    if (!bin) {
      this.addSpritesheet(this.createSpritesheet());
      bin = this.getCurrentSpritesheet().shelf.packOne(
        scaledMeasurement.x + 2,
        scaledMeasurement.y + 2,
      );
    }
    // At a fractional pixel ratio a bin can start between atlas pixels, which blurs the sprite.
    const atlasX = Math.ceil(bin.x * DEVICE_PIXEL_RATIO);
    const atlasY = Math.ceil(bin.y * DEVICE_PIXEL_RATIO);
    const drawX = atlasX / DEVICE_PIXEL_RATIO;
    const drawY = atlasY / DEVICE_PIXEL_RATIO;
    const sprite = {
      hashKey,
      lat: 0,
      lng: 0,
      alpha: 1,
      width: Math.ceil(scaledMeasurement.x * DEVICE_PIXEL_RATIO),
      height: Math.ceil(scaledMeasurement.y * DEVICE_PIXEL_RATIO),
      anchorX: scaledMeasurement.offsetX * DEVICE_PIXEL_RATIO,
      anchorY: scaledMeasurement.offsetY * DEVICE_PIXEL_RATIO,
      x: atlasX,
      y: atlasY,
      mask: false,
      sheetIndex: this.getCurrentSpritesheet().index,
    };

    if (drawInfo.type === DrawInfoType.POI_IMAGE) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      drawInfo.image = img;
      img.onload = () => {
        MarkerContext.draw(drawInfo, this.getCurrentSpritesheet().context, drawX, drawY, scale);
        this.dirtySprites.add(sprite);

        // workearound to trigger a rerender
        useStore.getState().actions.toggleTimer();
        useStore.getState().actions.toggleTimer();
      };
      img.src = drawInfo.icon || '';
    } else {
      MarkerContext.draw(drawInfo, this.getCurrentSpritesheet().context, drawX, drawY, scale);
    }

    this.dirtySprites.add(sprite);
    return sprite;
  }

  createPOISprite(poi: PoiType, largeMode: boolean): Sprite {
    const hashKey = `poi:${poi.id}-${poi.markerColor?.hexNum}:${largeMode}`;
    let sprite = this.sprites[hashKey];

    if (sprite == null) {
      const drawInfo = createPoiDrawInfo(
        poi.id,
        poi.shortName ?? realSubstring(poi.name, 0, 3),
        poi.name,
        poi.latlng,
        poi.markerColor?.hex ?? 'white',
        true,
        poi.icon,
      );
      sprite = this.createMarker(drawInfo, hashKey, largeMode ? LARGE_MODE_SCALE : 1);
      this.sprites = { ...this.sprites, [hashKey]: sprite };
    }
    sprite.lat = poi.latlng.lat;
    sprite.lng = poi.latlng.lng;
    sprite._poi = poi;
    return sprite;
  }

  createStarterSprite(
    starter: StarterType,
    lat: number,
    lng: number,
    opacity: number,
    flagContent: FlagContent,
    largeMode: boolean,
    isSelected: boolean,
  ): Sprite {
    const hashKey = `starter:${starter.id}:${starter.markerColor.hexNum}:${starter.name}:${starter.startNumber}:${flagContent}:${largeMode}:${isSelected}`;
    let sprite = this.sprites[hashKey];
    if (sprite == null) {
      const drawInfo = createStarterDrawInfo(starter, { lat, lng }, flagContent);
      const scale = (largeMode ? LARGE_MODE_SCALE : 1) * (isSelected ? 1 : UNSELECTED_SCALE);
      sprite = this.createMarker(drawInfo, hashKey, scale);
      this.sprites = { ...this.sprites, [hashKey]: sprite };
    }
    sprite.lat = lat;
    sprite.lng = lng;
    sprite.alpha = opacity;
    sprite._starter = starter;
    return sprite;
  }

  /** Returns only the spritesheets that have a usable texture. */
  updateDirtySpritesheets(device: Device): Array<SpriteSheet> {
    const dirtySpritesheets = new Set<SpriteSheet>();
    for (const sprite of this.dirtySprites) {
      dirtySpritesheets.add(this.spritesheets[sprite.sheetIndex]);
    }
    this.dirtySprites.clear();

    for (const spritesheet of this.spritesheets) {
      // The canvas size never changes, so an atlas is allocated once per device and
      // afterwards only re-uploaded. Reallocating leaks GPU memory until the driver
      // refuses to hand out another texture.
      if (spritesheet.texture != null && spritesheet.device === device) {
        if (dirtySpritesheets.has(spritesheet)) {
          spritesheet.texture.copyExternalImage({ image: spritesheet.canvas });
        }
        continue;
      }

      spritesheet.texture?.destroy();
      spritesheet.texture = null;
      spritesheet.device = null;

      try {
        const texture = device.createTexture({
          width: spritesheet.canvas.width,
          height: spritesheet.canvas.height,
        });
        texture.copyExternalImage({ image: spritesheet.canvas });
        spritesheet.texture = texture;
        spritesheet.device = device;
      } catch (error) {
        // Out of GPU memory. Drop this sheet's layer instead of killing the player,
        // and retry on the next update.
        console.warn('Could not allocate spritesheet texture', error);
      }
    }

    return this.spritesheets.filter((spritesheet) => spritesheet.texture != null);
  }
}

export default SpriteManager;
